'use client';

import { useTranslations } from 'next-intl';
import { type FC, type KeyboardEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { Textarea } from '@/app/components/ui/textarea';
import { useToast } from '@/app/components/ui/use-toast';
import { MAX_PROMPT_LENGTH } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import { cn } from '@/app/utils';
import { tasksListRemixStyles, type ViduV1RemixStyle } from '../../apis';

const STYLE_SELECTOR_SKELETON_COUNT = 8;

export type StyleSelectorOption = ViduV1RemixStyle & { id: string };

interface StyleSelectorProps {
  textPrompt?: string;
  onTextPromptChange?: (value: string) => void;
  selectedStyle?: string;
  selectedStyleName?: string;
  onStyleChange: (style: StyleSelectorOption | undefined) => void;
  autoSelectFirstOption?: boolean;
  showTextPrompt?: boolean;
  mobileColumnCount?: 2 | 3;
}

export const StyleSelector: FC<StyleSelectorProps> = ({
  textPrompt,
  onTextPromptChange,
  selectedStyle,
  selectedStyleName,
  onStyleChange,
  autoSelectFirstOption = false,
  showTextPrompt = true,
  mobileColumnCount = 2,
}) => {
  const t = useTranslations();
  const { toast } = useToast();
  const selectedStyleTagRef = useRef<HTMLSpanElement>(null);
  const textPromptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedStyleTagWidth, setSelectedStyleTagWidth] = useState(0);

  const { data: remixStylesData, loading } = useFetch(
    () =>
      tasksListRemixStyles({
        query: {
          'pager.page': 0,
          'pager.pagesz': 100,
          target_type: 'image',
          platform: 'web',
        },
      }),
    {
      onSuccess: ({ error }) => {
        if (!error) return;

        toast({
          type: 'error',
          message: error.message || t('popup.toast.networkError'),
        });
      },
    },
  );

  const styleOptions = useMemo(
    () => remixStylesData?.data?.styles?.filter((style): style is StyleSelectorOption => Boolean(style.id)) || [],
    [remixStylesData?.data?.styles],
  );
  const selectedStyleLabel =
    selectedStyleName || styleOptions.find((option) => option.id === selectedStyle)?.name || selectedStyle;

  useEffect(() => {
    if (!autoSelectFirstOption || selectedStyle || !styleOptions[0]) return;

    onStyleChange(styleOptions[0]);
  }, [autoSelectFirstOption, onStyleChange, selectedStyle, styleOptions]);

  useLayoutEffect(() => {
    setSelectedStyleTagWidth(selectedStyleTagRef.current?.offsetWidth || 0);
  }, [selectedStyleLabel]);

  useLayoutEffect(() => {
    const textarea = textPromptTextareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, 116), 320)}px`;
  }, [selectedStyleLabel, textPrompt]);

  useEffect(() => {
    if (loading || !selectedStyle || styleOptions.some((option) => option.id === selectedStyle)) return;

    onStyleChange(undefined);
  }, [loading, onStyleChange, selectedStyle, styleOptions]);

  const handleTextPromptKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!selectedStyleLabel || (event.key !== 'Backspace' && event.key !== 'Delete')) return;

    const { selectionStart, selectionEnd } = event.currentTarget;
    if (selectionStart !== 0 || selectionEnd !== 0) return;

    event.preventDefault();
    onStyleChange(undefined);
  };

  return (
    <div className="flex flex-col gap-3">
      {showTextPrompt ? (
        <div className="flex flex-col gap-2">
          <span className="font-medium text-sm text-system-white leading-[1.4]">
            {t('tools.styleTransfer.textPrompt.label')}
          </span>
          <div className="max-h-[320px] min-h-[116px] w-full overflow-auto rounded-10 border border-input bg-system-white/5 px-3 py-2">
            <div className="relative min-h-full">
              {selectedStyleLabel ? (
                <span
                  ref={selectedStyleTagRef}
                  className="absolute top-0 left-0 z-10 flex max-w-full items-center gap-1 rounded-6 bg-system-white24 px-2 text-sm text-system-white leading-5"
                >
                  <span className="truncate">{selectedStyleLabel}</span>
                  <button
                    type="button"
                    className="shrink-0 text-system-white80"
                    onClick={() => onStyleChange(undefined)}
                    aria-label={selectedStyleLabel}
                  >
                    <BaseLineClose className="size-3.5" />
                  </button>
                </span>
              ) : null}
              <Textarea
                ref={textPromptTextareaRef}
                value={textPrompt || ''}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value.length >= MAX_PROMPT_LENGTH) {
                    toast({
                      type: 'warning',
                      message: t('create.createForm.error.invalidLength', { limit: MAX_PROMPT_LENGTH }),
                    });
                  }
                  onTextPromptChange?.(value);
                }}
                onKeyDown={handleTextPromptKeyDown}
                placeholder={selectedStyleLabel ? '' : t('tools.styleTransfer.textPrompt.placeholder')}
                maxLength={MAX_PROMPT_LENGTH}
                rows={3}
                className="block min-h-[100px] w-full resize-none overflow-hidden rounded-none border-none bg-transparent p-0 text-sm text-system-white ring-offset-transparent placeholder:text-system-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
                style={{ textIndent: selectedStyleTagWidth ? selectedStyleTagWidth + 4 : 0 }}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <span className="font-medium text-sm text-system-white leading-[1.4]">
          {t('tools.styleTransfer.referenceStyle.label')}
        </span>
        <div className="rounded-12 border border-system-white12 bg-[#090E10] p-4 sm:p-3">
          <div
            className={cn(
              'grid grid-cols-4 gap-3 md:grid-cols-3',
              mobileColumnCount === 3 ? 'sm:grid-cols-3 sm:gap-2' : 'sm:grid-cols-2 sm:gap-3',
            )}
          >
            {loading
              ? Array.from({ length: STYLE_SELECTOR_SKELETON_COUNT }).map((_, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: <这里是 loading 的骨架屏>
                  <div key={index} className="aspect-square animate-pulse rounded-8 bg-system-white08" />
                ))
              : null}
            {styleOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={selectedStyle === option.id}
                onClick={() => {
                  onStyleChange(selectedStyle === option.id ? undefined : option);
                }}
                className={cn(
                  'group relative aspect-square overflow-hidden rounded-8 border border-transparent bg-system-white08 text-left transition-all duration-200',
                  'hover:-translate-y-0.5',
                  selectedStyle === option.id && 'border-system-white shadow-[0_0_0_2px_rgba(255,255,255,0.18)]',
                )}
              >
                {option.image_url ? (
                  <img
                    src={option.image_url}
                    alt={option.name || ''}
                    className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <span className="absolute inset-0 bg-system-white08" />
                )}
                <span
                  className={cn(
                    'absolute inset-x-0 bottom-0 flex min-h-[58px] items-end bg-gradient-to-t from-black/80 via-black/45 to-transparent px-1 pb-1',
                    mobileColumnCount === 3 && 'sm:min-h-[42px]',
                  )}
                >
                  <span className="truncate font-normal text-system-white text-xs leading-[1.25]">
                    {option.name || option.id}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
